import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Plus,
  Edit3,
  Trash2,
  Save,
  ArrowLeft,
  Search,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

// 📝 COMMENT: Import your actual web Redux actions and services
import { userPreferences } from '../../actions/actions';
import { putUserPreferences } from '../../services/leadService';

// 📝 COMMENT: Types for customization items - ENHANCED to match mobile
interface PreferenceItem {
  _id?: string;
  name: string;
  value: string;
  color?: string;
  isDefault?: boolean;
  isSaleStatus?: boolean;
  type?: string;
  label?: string;
  isRequired?: boolean;
  options?: { name: string; value: string }[];
  read_only?: boolean;
  placeholder?: string;
}

interface CustomizationCardProps {
  title: string;
  type: string;
  description: string;
  icon: React.ReactNode;
  color?: string;
}

// 📝 COMMENT: Color options - exact match from mobile COLOR_OPTIONS
const COLOR_OPTIONS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Slate', value: '#64748b' }
];

// 📝 COMMENT: Form field types - EXACT MATCH from your mobile implementation
const FORM_FIELD_TYPES = [
  { name: 'Text', value: 'text' },
  { name: 'Name', value: 'name' },
  { name: 'Email', value: 'email' },
  { name: 'Phone', value: 'phone' },
  { name: 'Number', value: 'number' },
  { name: 'Amount', value: 'amount' },
  { name: 'Date', value: 'date' },
  { name: 'Time', value: 'time' },
  { name: 'Selection (Dropdown)', value: 'selection' },
  { name: 'Multi-Selection', value: 'multi-selection' },
  { name: 'URL', value: 'url' },
  { name: 'Location', value: 'location' },
  { name: 'Textarea', value: 'textarea' }
];

type ViewMode = 'list' | 'add' | 'edit';

const CustomizationCard: React.FC<CustomizationCardProps> = ({ 
  title, 
  type, 
  description, 
  icon, 
  color = '#3b82f6' 
}) => {
  const dispatch = useDispatch();
  
  // 📝 COMMENT: Use your actual web Redux selectors
  const userPrefs = useSelector(
    (state: any) => state?.rootReducers?.user?.userPreferences || {}
  );

  // 📝 COMMENT: State management
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingItem, setEditingItem] = useState<PreferenceItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<PreferenceItem | null>(null);

  // 📝 COMMENT: Form state for add/edit - ENHANCED with all mobile properties
  const [formData, setFormData] = useState<PreferenceItem>({
    name: '',
    value: '',
    color: COLOR_OPTIONS[0].value,
    isDefault: false,
    isSaleStatus: false,
    type: 'text',
    label: '',
    isRequired: false,
    options: [],
    read_only: false,
    placeholder: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // 📝 COMMENT: State for managing dropdown options
  const [optionText, setOptionText] = useState('');
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);

  // 📝 COMMENT: Get current preferences based on type
  const currentPreferences = useMemo(() => {
    return userPrefs[type] || [];
  }, [userPrefs, type]);

  // 📝 COMMENT: Filter preferences by search term
  const filteredPreferences = useMemo(() => {
    if (!searchTerm.trim()) return currentPreferences;
    return currentPreferences.filter((item: PreferenceItem) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [currentPreferences, searchTerm]);

  // 📝 COMMENT: Initialize form when editing - ENHANCED for all field types
  useEffect(() => {
    if (viewMode === 'edit' && editingItem) {
      setFormData({ ...editingItem });
    } else if (viewMode === 'add') {
      const defaultFormData: PreferenceItem = {
        name: '',
        value: '',
        color: COLOR_OPTIONS[0].value,
        isDefault: false,
        isSaleStatus: false,
        read_only: false,
        placeholder: ''
      };
      
      // 📝 COMMENT: Add specific fields for lead form fields
      if (type === 'customForm') {
        defaultFormData.type = 'text';
        defaultFormData.label = '';
        defaultFormData.isRequired = false;
        defaultFormData.options = [];
      }
      
      setFormData(defaultFormData);
    }
    
    // Reset form errors and option state
    setFormErrors({});
    setOptionText('');
    setEditingOptionIndex(null);
  }, [viewMode, editingItem, type]);

  // 📝 COMMENT: Handle dropdown options management
  const addOption = useCallback(() => {
    if (!optionText.trim()) return;
    
    const newOption = {
      name: optionText.trim(),
      value: optionText.trim().toLowerCase().replace(/\s+/g, '_')
    };
    
    if (editingOptionIndex !== null) {
      // Edit existing option
      const updatedOptions = [...(formData.options || [])];
      updatedOptions[editingOptionIndex] = newOption;
      setFormData(prev => ({ ...prev, options: updatedOptions }));
      setEditingOptionIndex(null);
    } else {
      // Add new option
      setFormData(prev => ({ 
        ...prev, 
        options: [...(prev.options || []), newOption] 
      }));
    }
    
    setOptionText('');
  }, [optionText, editingOptionIndex, formData.options]);

  const editOption = useCallback((index: number) => {
    const option = formData.options?.[index];
    if (option) {
      setOptionText(option.name);
      setEditingOptionIndex(index);
    }
  }, [formData.options]);

  const removeOption = useCallback((index: number) => {
    const updatedOptions = formData.options?.filter((_, i) => i !== index) || [];
    setFormData(prev => ({ ...prev, options: updatedOptions }));
  }, [formData.options]);

  // 📝 COMMENT: Form validation - ENHANCED to match mobile validation
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      errors.name = 'Name is required';
    }

    // 📝 COMMENT: Additional validation for lead form fields
    if (type === 'customForm') {
      if (!formData.label?.trim()) {
        errors.label = 'Label is required';
      }
      if (!formData.type) {
        errors.type = 'Field type is required';
      }
      
      // 📝 COMMENT: Validate options for selection fields
      if (['selection', 'multi-selection'].includes(formData.type || '') && 
          (!formData.options || formData.options.length === 0)) {
        errors.options = 'At least one option is required for selection fields';
      }
    }

    // 📝 COMMENT: Check for duplicate names
    const duplicateName = currentPreferences.find(
      (item: PreferenceItem) => 
        item.name.toLowerCase() === formData.name.toLowerCase() &&
        (viewMode !== 'edit' || item._id !== formData._id)
    );

    if (duplicateName) {
      errors.name = `${title} with this name already exists`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, currentPreferences, title, viewMode, type]);

  // 📝 COMMENT: Handle form submission - MATCHES MOBILE PATTERN EXACTLY
  // Mobile does: dispatch(updateUserPrefrence()) which calls UserApi().updatePreference()  
  // Web does: dispatch(userPreferences()) + putUserPreferences() - SAME RESULT!
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors and try again');
      return;
    }

    setIsLoading(true);
    try {
      let updatedPreferences;
      
      if (viewMode === 'edit') {
        // 📝 COMMENT: Update existing item
        updatedPreferences = currentPreferences.map((item: PreferenceItem) =>
          item._id === formData._id || item.value === formData.value 
            ? { ...formData }
            : item
        );
      } else {
        // 📝 COMMENT: Add new item - generate auto value from name like mobile
        const newItem = {
          ...formData,
          value: formData.name.trim().toLowerCase().replace(/\s+/g, '_'),
          _id: `${type}_${Date.now()}`
        };
        updatedPreferences = [...currentPreferences, newItem];
      }

      // 📝 COMMENT: Update Redux state using web pattern (step 1 of 2)
      const updatedUserPrefs = {
        ...userPrefs,
        [type]: updatedPreferences
      };
      
      dispatch(userPreferences(updatedUserPrefs));

      // 📝 COMMENT: Update backend using web service (step 2 of 2) 
      // This is equivalent to mobile's UserApi().updatePreference() call
      const response = await putUserPreferences({
        [type]: updatedPreferences
      });

      if (response?.status) {
        toast.success(`${title} ${viewMode === 'edit' ? 'updated' : 'created'} successfully`);
        setViewMode('list');
        setEditingItem(null);
        // Reset form state
        setOptionText('');
        setEditingOptionIndex(null);
      } else {
        throw new Error('Failed to save item');
      }

    } catch (error) {
      console.error('Error saving preference:', error);
      toast.error(`Failed to ${viewMode === 'edit' ? 'update' : 'create'} ${title.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, viewMode, currentPreferences, type, title, dispatch, userPrefs]);

  // 📝 COMMENT: Handle delete - MATCHES MOBILE PATTERN (Redux + API)
  const handleDelete = useCallback(async (item: PreferenceItem) => {
    if (item.isDefault) {
      toast.error('Cannot delete default items');
      return;
    }

    setIsLoading(true);
    try {
      const updatedPreferences = currentPreferences.filter(
        (pref: PreferenceItem) => pref._id !== item._id && pref.value !== item.value
      );

      // 📝 COMMENT: Update Redux state using web pattern (step 1 of 2)
      const updatedUserPrefs = {
        ...userPrefs,
        [type]: updatedPreferences
      };
      
      dispatch(userPreferences(updatedUserPrefs));

      // 📝 COMMENT: Update backend using web service (step 2 of 2)
      // This matches mobile's API call pattern
      const response = await putUserPreferences({
        [type]: updatedPreferences
      });

      if (response?.status) {
        toast.success(`${title} deleted successfully`);
        setDeleteConfirm(null);
      } else {
        throw new Error('Failed to delete item');
      }

    } catch (error) {
      console.error('Error deleting preference:', error);
      toast.error('Failed to delete item');
    } finally {
      setIsLoading(false);
    }
  }, [currentPreferences, type, title, dispatch, userPrefs]);

  // 📝 COMMENT: Handle form field changes
  const handleFieldChange = useCallback((field: keyof PreferenceItem, value: any) => {
    setFormData(prev => {
      const updated = { ...prev };
      
      if (field === 'name') {
        updated.name = value;
        // Auto-generate value from name like mobile does
        updated.value = value.trim().toLowerCase().replace(/\s+/g, '_');
        
        // For customForm, also set label if empty
        if (type === 'customForm' && !updated.label) {
          updated.label = value;
        }
      } else {
        // Use type assertion to handle different field types
        (updated as any)[field] = value;
      }

      return updated;
    });

    // Clear field error
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [formErrors, type]);

  // 📝 COMMENT: Render card header
  const renderCardHeader = () => (
    <div 
      onClick={() => setIsExpanded(!isExpanded)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px',
        cursor: 'pointer',
        borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          backgroundColor: `${color}15`,
          borderRadius: '12px'
        }}>
          {icon}
        </div>
        <div>
          <h3 style={{ 
            margin: '0 0 4px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#111827' 
          }}>
            {title}
          </h3>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#6b7280' 
          }}>
            {description}
          </p>
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{
          fontSize: '12px',
          fontWeight: '500',
          color: color,
          backgroundColor: `${color}15`,
          padding: '4px 8px',
          borderRadius: '12px'
        }}>
          {currentPreferences.length} items
        </span>
        
        <div style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </div>
  );

  // 📝 COMMENT: Render expanded content
  const renderExpandedContent = () => {
    if (!isExpanded) return null;

    if (viewMode === 'add' || viewMode === 'edit') {
      return renderForm();
    }
    return renderList();
  };

  // 📝 COMMENT: Render preferences list
  const renderList = () => (
    <div style={{ padding: '20px 24px' }}>
      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        gap: '12px'
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, maxWidth: '250px' }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }} />
          <input
            type="text"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px 6px 32px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none'
            }}
          />
        </div>

        {/* Add Button */}
        <button
          onClick={() => setViewMode('add')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: color,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <Plus size={14} />
          Add {title}
        </button>
      </div>

      {/* List Items */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {filteredPreferences.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280'
          }}>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
              {searchTerm.trim() 
                ? `No ${title.toLowerCase()} match your search.`
                : `No ${title.toLowerCase()} created yet.`
              }
            </p>
            {!searchTerm.trim() && (
              <button
                onClick={() => setViewMode('add')}
                style={{
                  backgroundColor: color,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Add First {title}
              </button>
            )}
          </div>
        ) : (
          filteredPreferences.map((item: PreferenceItem, index: number) => (
            <div
              key={item._id || item.value || index}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                marginBottom: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
            >
              {/* Color Indicator */}
              {item.color && (
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  borderRadius: '50%',
                  marginRight: '10px'
                }} />
              )}

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#111827' 
                  }}>
                    {item.label || item.name}
                  </span>
                  
                  {item.isDefault && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#059669',
                      backgroundColor: '#d1fae5',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Default
                    </span>
                  )}
                  
                  {item.isSaleStatus && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Sale Status
                    </span>
                  )}
                  
                  {type === 'customForm' && item.isRequired && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Required
                    </span>
                  )}
                </div>
                
                {type === 'customForm' && item.type && (
                  <p style={{ 
                    margin: '2px 0 0 0', 
                    fontSize: '12px', 
                    color: '#6b7280' 
                  }}>
                    Type: {item.type} | Value: {item.value}
                  </p>
                )}
                
                {(type === 'labels' || type === 'status') && item.value && item.value !== item.name && (
                  <p style={{ 
                    margin: '2px 0 0 0', 
                    fontSize: '12px', 
                    color: '#6b7280' 
                  }}>
                    Value: {item.value}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => {
                    setEditingItem(item);
                    setViewMode('edit');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  <Edit3 size={12} className="text-gray-600" />
                </button>

                <button
                  onClick={() => setDeleteConfirm(item)}
                  disabled={item.isDefault}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: item.isDefault ? '#f9fafb' : '#ffffff',
                    border: `1px solid ${item.isDefault ? '#e5e7eb' : '#fecaca'}`,
                    borderRadius: '4px',
                    cursor: item.isDefault ? 'not-allowed' : 'pointer',
                    opacity: item.isDefault ? 0.5 : 1
                  }}
                >
                  <Trash2 size={12} className={item.isDefault ? 'text-gray-400' : 'text-red-500'} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // 📝 COMMENT: Render add/edit form
  const renderForm = () => (
    <div style={{ padding: '20px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setViewMode('list')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#6b7280',
            marginBottom: '12px'
          }}
        >
          <ArrowLeft size={14} />
          Back to List
        </button>

        <h4 style={{ 
          margin: '0 0 4px 0', 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#111827' 
        }}>
          {viewMode === 'edit' ? `Edit ${title}` : `Add New ${title}`}
        </h4>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px'
        }}>
          {/* Name Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '13px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder={`Enter ${title.toLowerCase()} name`}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${formErrors.name ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none'
              }}
            />
            {formErrors.name && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                fontSize: '11px',
                color: '#dc2626'
              }}>
                <AlertCircle size={12} />
                {formErrors.name}
              </div>
            )}
          </div>

          {/* Lead Form Fields - Additional Fields */}
          {type === 'customForm' && (
            <>
              {/* Label Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Label <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.label || ''}
                  onChange={(e) => handleFieldChange('label', e.target.value)}
                  placeholder="Enter field label"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: `1px solid ${formErrors.label ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
                {formErrors.label && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '11px',
                    color: '#dc2626'
                  }}>
                    <AlertCircle size={12} />
                    {formErrors.label}
                  </div>
                )}
              </div>

              {/* Field Type */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Field Type <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={formData.type || 'text'}
                  onChange={(e) => {
                    handleFieldChange('type', e.target.value);
                    // Clear options when changing field type
                    if (!['selection', 'multi-selection'].includes(e.target.value)) {
                      setFormData(prev => ({ ...prev, options: [] }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: `1px solid ${formErrors.type ? '#dc2626' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                >
                  {FORM_FIELD_TYPES.map((fieldType) => (
                    <option key={fieldType.value} value={fieldType.value}>
                      {fieldType.name}
                    </option>
                  ))}
                </select>
                {formErrors.type && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '11px',
                    color: '#dc2626'
                  }}>
                    <AlertCircle size={12} />
                    {formErrors.type}
                  </div>
                )}
              </div>

              {/* Options Management for Selection Fields */}
              {['selection', 'multi-selection'].includes(formData.type || '') && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Options <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  
                  {/* Add Option Input */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="text"
                      value={optionText}
                      onChange={(e) => setOptionText(e.target.value)}
                      placeholder="Enter option text"
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '12px',
                        outline: 'none'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={addOption}
                      disabled={!optionText.trim()}
                      style={{
                        backgroundColor: optionText.trim() ? color : '#d1d5db',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: optionText.trim() ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {editingOptionIndex !== null ? 'Update' : 'Add'}
                    </button>
                  </div>

                  {/* Options List */}
                  {formData.options && formData.options.length > 0 && (
                    <div style={{
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      padding: '8px',
                      maxHeight: '120px',
                      overflowY: 'auto'
                    }}>
                      {formData.options.map((option, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '4px 6px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '3px',
                            marginBottom: '4px',
                            fontSize: '12px'
                          }}
                        >
                          <span style={{ flex: 1 }}>{option.name}</span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              type="button"
                              onClick={() => editOption(index)}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px',
                                color: '#6b7280'
                              }}
                            >
                              <Edit3 size={10} />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeOption(index)}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px',
                                color: '#dc2626'
                              }}
                            >
                              <X size={10} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {formErrors.options && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      marginTop: '4px',
                      fontSize: '11px',
                      color: '#dc2626'
                    }}>
                      <AlertCircle size={12} />
                      {formErrors.options}
                    </div>
                  )}
                </div>
              )}

              {/* Placeholder Field */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Placeholder Text
                </label>
                <input
                  type="text"
                  value={formData.placeholder || ''}
                  onChange={(e) => handleFieldChange('placeholder', e.target.value)}
                  placeholder="Enter placeholder text (optional)"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Required Checkbox */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={!!formData.isRequired}
                    onChange={(e) => handleFieldChange('isRequired', e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  Mark as Required Field
                </label>
              </div>
            </>
          )}

          {/* Color Field (for labels and status) */}
          {(type === 'labels' || type === 'status') && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Color
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(32px, 1fr))',
                gap: '6px',
                maxWidth: '280px'
              }}>
                {COLOR_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleFieldChange('color', option.value)}
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: option.value,
                      border: formData.color === option.value ? '2px solid #374151' : '1px solid #e5e7eb',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    title={option.name}
                  >
                    {formData.color === option.value && (
                      <CheckCircle2 
                        size={14} 
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#ffffff',
                          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Status-specific fields */}
          {type === 'status' && (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={!!formData.isDefault}
                    onChange={(e) => handleFieldChange('isDefault', e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  Set as Default Status
                </label>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={!!formData.isSaleStatus}
                    onChange={(e) => handleFieldChange('isSaleStatus', e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  Mark as Sale Status
                </label>
              </div>
            </>
          )}

          {/* Preview */}
          {formData.name && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Preview
              </label>
              <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '12px'
              }}>
                {/* Field Preview */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px'
                }}>
                  {formData.color && (type === 'labels' || type === 'status') && (
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: formData.color,
                      borderRadius: '50%'
                    }} />
                  )}
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>
                    {type === 'customForm' ? formData.label || formData.name : formData.name}
                  </span>
                  {formData.isDefault && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#059669',
                      backgroundColor: '#d1fae5',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Default
                    </span>
                  )}
                  {formData.isSaleStatus && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Sale Status
                    </span>
                  )}
                  {type === 'customForm' && formData.isRequired && (
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '500',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      padding: '1px 4px',
                      borderRadius: '3px'
                    }}>
                      Required
                    </span>
                  )}
                </div>

                {/* Form Field Preview for customForm */}
                {type === 'customForm' && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Field Type: <strong>{FORM_FIELD_TYPES.find(t => t.value === formData.type)?.name}</strong>
                      {formData.placeholder && ` • Placeholder: "${formData.placeholder}"`}
                    </div>
                    
                    {/* Mock Input Preview */}
                    {formData.type === 'textarea' ? (
                      <textarea
                        disabled
                        placeholder={formData.placeholder || `Enter ${formData.label || formData.name}`}
                        style={{
                          width: '100%',
                          height: '60px',
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: '#f9fafb',
                          resize: 'none'
                        }}
                      />
                    ) : ['selection', 'multi-selection'].includes(formData.type || '') ? (
                      <div>
                        <select
                          disabled
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: '#f9fafb'
                          }}
                        >
                          <option>{formData.placeholder || `Select ${formData.label || formData.name}`}</option>
                          {formData.options?.map((option, i) => (
                            <option key={i} value={option.value}>{option.name}</option>
                          ))}
                        </select>
                        {formData.options && formData.options.length > 0 && (
                          <div style={{
                            fontSize: '10px',
                            color: '#6b7280',
                            marginTop: '4px'
                          }}>
                            Options: {formData.options.map(opt => opt.name).join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <input
                        type={['number', 'amount'].includes(formData.type || '') ? 'number' : 
                              ['email'].includes(formData.type || '') ? 'email' :
                              ['date'].includes(formData.type || '') ? 'date' :
                              ['time'].includes(formData.type || '') ? 'time' : 'text'}
                        disabled
                        placeholder={formData.placeholder || `Enter ${formData.label || formData.name}`}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: '#f9fafb'
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            paddingTop: '12px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              disabled={isLoading}
              style={{
                backgroundColor: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: color,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              <Save size={14} />
              {isLoading 
                ? `${viewMode === 'edit' ? 'Updating' : 'Creating'}...`
                : `${viewMode === 'edit' ? 'Update' : 'Create'} ${title}`
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      overflow: 'hidden'
    }}>
      {renderCardHeader()}
      {renderExpandedContent()}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '350px',
            width: '90%'
          }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#111827' 
            }}>
              Delete {title}
            </h4>
            <p style={{ 
              margin: '0 0 16px 0', 
              fontSize: '13px', 
              color: '#6b7280' 
            }}>
              Are you sure you want to delete "{deleteConfirm.label || deleteConfirm.name}"? This action cannot be undone.
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isLoading}
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isLoading}
                style={{
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomizationCard;